import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../../../API';
import { useMutation, useQuery } from 'react-query';
import routes from '../../../Config/routes';
import CampaignDetailsUI from './campaigndetailsUI';
import {
  Button,
  Divider,
  Drawer,
  Box,
  Badge,
  IconButton,
  Backdrop,
  CircularProgress,
  Typography,
  Stack,
  Chip,
  Card,
  Fade,
  Avatar,
  Tooltip,
  Alert,
  AlertTitle,
  LinearProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { globalStyles } from '../../../Utils/Styles';
import LeftColumnSearch from '../Search/leftColumnSearch';
import {
  getPlatformAndPromotion,
  platformToFollowing,
  platformToLink,
  sortCreators,
} from '../../../Utils/constants';
import useIsDesktop from '../../../Hooks/useIsDesktop';
import useAuth from '../../../Hooks/use-auth';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'sonner';
import AddBalanceDialog from '../../../Components/tools/addBalance';

const RightDrawerWidth = '28rem';

/* ─────────────────────────────────────────────────
   FUNDS SUMMARY CARD
───────────────────────────────────────────────── */
const FundsSummary = ({
  isLoadingFunds,
  hasAnyFunds,
  hasBlitzPayAccount,
  balance,
  spendingAvailable,
  maxAffordable,
  totalCost,
  onAddBalance,
  theme,
  isTcc,
}) => {
  if (isLoadingFunds) {
    return (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="body2">Checking your available funds…</Typography>
      </Alert>
    );
  }

  const canAfford = totalCost <= maxAffordable;
  const shortfall = totalCost - maxAffordable;

  if (!isTcc && !hasAnyFunds) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2, borderRadius: 2 }}
        action={
          <Button color="inherit" size="small" startIcon={<AddCircleIcon />} onClick={onAddBalance}>
            Add Balance
          </Button>
        }
      >
        <AlertTitle>No Funds Available</AlertTitle>
        <Typography variant="body2">
          You need to add balance before adding creators.
          {hasBlitzPayAccount && ' Your BlitzPay spending power is also at $0.'}
        </Typography>
      </Alert>
    );
  }

  if (!isTcc && totalCost > 0 && !canAfford) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 2, borderRadius: 2 }}
        icon={<WarningAmberIcon />}
        action={
          <Button color="inherit" size="small" startIcon={<AddCircleIcon />} onClick={onAddBalance}>
            Add Balance
          </Button>
        }
      >
        <AlertTitle>Insufficient Funds</AlertTitle>
        <Typography variant="body2">
          You need <strong>${totalCost.toFixed(2)}</strong> but only have{' '}
          <strong>${maxAffordable.toFixed(2)}</strong> available. Add{' '}
          <strong>${shortfall.toFixed(2)}</strong> to proceed.
        </Typography>
      </Alert>
    );
  }

  // Funds OK — always show balance so the user can see what they're working with
  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        overflow: 'visible',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.success.main, 0.04),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            mt: 0.5,
            bgcolor: alpha(theme.palette.success.main, 0.12),
            color: 'success.main',
          }}
        >
          <AccountBalanceWalletIcon sx={{ fontSize: '1.1rem' }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Available funds
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={700} color="success.main">
              ${maxAffordable.toLocaleString()}
            </Typography>
            {totalCost > 0 && (
              <Typography variant="caption" color="text.secondary">
                · ${totalCost.toFixed(2)} needed
              </Typography>
            )}
          </Stack>
          {totalCost > 0 && (
            <LinearProgress
              variant="determinate"
              value={Math.min((totalCost / maxAffordable) * 100, 100)}
              color={canAfford ? 'success' : 'error'}
              sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
            />
          )}

          {/* Balance breakdown — always visible so users know what they have */}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
            <Chip
              label={`Balance: $${balance.toLocaleString()}`}
              size="small"
              variant="outlined"
              color="success"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
            {hasBlitzPayAccount && spendingAvailable > 0 && (
              <Chip
                label={`Spending Power: $${spendingAvailable.toLocaleString()}`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
          </Stack>
        </Box>
        <Tooltip title="Add balance">
          <IconButton size="small" onClick={onAddBalance} sx={{ color: 'text.secondary' }}>
            <AddCircleIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Card>
  );
};

/* ─────────────────────────────────────────────────
   DRAWER HEADER
───────────────────────────────────────────────── */
const DrawerHeader = ({ creatorCount, campaignName, theme }) => (
  <Box sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
      <Typography variant="h6" fontWeight={700} color="text.primary">
        Creator Roster
      </Typography>
      {creatorCount > 0 && (
        <Chip
          icon={<PeopleAltOutlinedIcon sx={{ fontSize: '0.8rem !important' }} />}
          label={`${creatorCount} selected`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: '0.72rem', height: 24 }}
        />
      )}
    </Stack>
    {campaignName && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {campaignName}
      </Typography>
    )}
  </Box>
);

/* ─────────────────────────────────────────────────
   SELECTED CREATOR PILL ROW
───────────────────────────────────────────────── */
const SelectedCreatorRow = ({ creator, onRemove, theme }) => {
  const name = creator.creator || creator.name || 'Unknown';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        mb: 1,
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: theme.palette.primary.main },
      }}
    >
      <Avatar
        src={creator.pfphref || undefined}
        sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: theme.palette.primary.main, flexShrink: 0 }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>
      <Typography
        variant="body2"
        fontWeight={500}
        color="text.primary"
        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {name}
      </Typography>
      <Tooltip title="Remove creator">
        <IconButton
          size="small"
          onClick={() => onRemove(creator.creator)}
          sx={{
            color: 'text.disabled',
            '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) },
          }}
        >
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/* ─────────────────────────────────────────────────
   EMPTY ROSTER STATE
───────────────────────────────────────────────── */
const EmptyRoster = ({ theme }) => (
  <Box
    sx={{
      py: 5,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      borderRadius: 2,
      border: `1.5px dashed ${theme.palette.divider}`,
      bgcolor: alpha(theme.palette.primary.main, 0.02),
    }}
  >
    <PersonRemoveIcon sx={{ fontSize: '2rem', color: 'text.disabled' }} />
    <Typography variant="body2" color="text.secondary" textAlign="center">
      Select creators from the left panel to add them to this campaign.
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────────
   CONFIRM SECTION
───────────────────────────────────────────────── */
const ConfirmSection = ({ disabled, onClick, count, canAfford, hasAnyFunds, theme, isTcc }) => {
  const blocked = disabled || (!isTcc && (!canAfford || !hasAnyFunds));

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
      {count > 0 && (isTcc || (canAfford && hasAnyFunds)) && (
        <Fade in>
          <Card
            elevation={0}
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              bgcolor: alpha(theme.palette.success.main, 0.06),
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: '1rem' }} />
            <Typography variant="body2" color="success.main" fontWeight={500}>
              {count} creator{count !== 1 ? 's' : ''} ready to confirm
            </Typography>
          </Card>
        </Fade>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={blocked}
        onClick={onClick}
        startIcon={<CheckCircleOutlineIcon />}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          py: 1.25,
          boxShadow: blocked ? 'none' : `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
          '&:hover': {
            boxShadow: blocked ? 'none' : `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
          },
        }}
      >
        Confirm Creator Changes
      </Button>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
        {isTcc
          ? 'Changes will be applied immediately'
          : !hasAnyFunds
          ? 'Add balance to continue'
          : !canAfford && count > 0
          ? 'Add balance to cover creator costs'
          : 'Changes will be applied immediately'}
      </Typography>
    </Box>
  );
};

/* ─────────────────────────────────────────────────
   LOADING BACKDROP
───────────────────────────────────────────────── */
const UpdateBackdrop = ({ open }) => (
  <Backdrop
    open={open}
    sx={{ zIndex: (t) => t.zIndex.modal + 3, bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
  >
    <Fade in={open}>
      <Stack alignItems="center" spacing={2}>
        <CircularProgress size={48} thickness={4} sx={{ '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
          Updating campaign...
        </Typography>
      </Stack>
    </Fade>
  </Backdrop>
);

/* ─────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────── */
const AddCreators = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLargeScreen = useIsDesktop();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const isTcc = currentUser?.company_name === "TCC" || currentUser?.company_id === 1;

  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectedCreatorsData, setSelectedCreatorsData] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [triedNext, setTriedNext] = useState(false);
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);

  /* ── Campaign + creator queries ── */
  const { data: campaignDetails, error } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => client.campaigns.fetch(campaignId),
    enabled: !!campaignId,
    refetchOnWindowFocus: false,
  });

  const {
    data: creatorList,
    isFetching: creatorIsLoading,
    error: creatorError,
    isError: creatorIsError,
    refetch: refetchCreators,
  } = useQuery({
    queryKey: ['creators'],
    queryFn: async () => {
      const creators = await client.creators.list({ include_reviews: true, include_public: true });
      return sortCreators(creators);
    },
    refetchOnWindowFocus: false,
    initialData: [],
    retry: 1,
    staleTime: 0,
    cacheTime: 300000,
  });

  /* ── Funds queries ── */
  const {
    data: balanceData,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useQuery(
    ['myBalance'],
    () => client.balanceLedger.fetchMyBalance(),
    { enabled: !!currentUser?.id, staleTime: 30000, refetchOnWindowFocus: false }
  );

  const { data: companyInfo, isLoading: companyLoading } = useQuery(
    ['company', currentUser?.company_id],
    () => client.companies.listFetch({ id: currentUser?.company_id }),
    { enabled: !!currentUser?.company_id, staleTime: 60000, refetchOnWindowFocus: false }
  );

  const hasBlitzPayAccount = companyInfo?.blitzpay_account === true;

  const { data: spendingInfo, isLoading: spendingLoading } = useQuery(
    ['mySpendingInfo'],
    () => client.teams.getMySpendingInfo(),
    { enabled: hasBlitzPayAccount, staleTime: 60000, refetchOnWindowFocus: false }
  );

  /* ── Derived fund values ── */
  const balance = parseFloat(balanceData?.balance || 0);
  const spendingAvailable = hasBlitzPayAccount
    ? parseFloat(spendingInfo?.total_available_funds || 0)
    : 0;
  const maxAffordable = Math.floor(balance + spendingAvailable);
  const hasAnyFunds = maxAffordable > 0;
  const isLoadingFunds = balanceLoading || companyLoading || (hasBlitzPayAccount && spendingLoading);

  /* ── Total cost of new creators ── */
  const totalNewCreatorCost = useMemo(
    () => selectedCreatorsData.reduce((sum, c) => sum + (parseFloat(c.adjustedPrice) || 0), 0),
    [selectedCreatorsData]
  );
  const canAfford = totalNewCreatorCost === 0 || totalNewCreatorCost <= maxAffordable;

  /* ── Locked creators already on campaign ── */
  const prevCreators = useMemo(() => {
    if (!campaignDetails) return new Set([]);
    return new Set(campaignDetails.creators.map((c) => c.id));
  }, [campaignDetails]);

  useEffect(() => {
    setSelectedItems((prev) => new Set([...prev, ...prevCreators]));
  }, [prevCreators]);

  /* ── Error handling ── */
  useEffect(() => {
    if (error) alert(`Error fetching campaign: ${error.response?.data?.error || error.message}`);
  }, [error]);

  useEffect(() => {
    if (creatorIsError && creatorError) {
      toast.error('Unable to load creators', {
        description: creatorError.response?.data?.error || creatorError.message || 'Failed to load creators.',
        action: { label: 'Retry', onClick: () => refetchCreators() },
      });
    }
  }, [creatorIsError, creatorError, refetchCreators]);

  /* ── Helpers ── */
  const getNextId = (videos) => videos.reduce((max, v) => Math.max(max, v.id), 0) + 1;

  const calculateBasePrice = (creator, videos = []) =>
    videos.reduce(
      (acc, video) =>
        acc +
        (creator[video.rateKey] || 0) +
        Array.from(video.crossPostingKeys).reduce((a, cp) => a + (creator[cp] || 0) / 4, 0),
      0
    );

  const calculateAdjustedPrice = (base, usageRights) => (usageRights ? base * 1.1 : base);

  /* ── Handlers ── */
  const handlePriceChange = (index, newPrice) => {
    setSelectedCreatorsData((prev) => {
      const updated = [...prev];
      updated[index].basePrice = parseFloat(newPrice) || 0;
      updated[index].adjustedPrice = calculateAdjustedPrice(updated[index].basePrice, updated[index].usageRights);
      return updated;
    });
  };

  const handleAddVideo = (index) => {
    setSelectedCreatorsData((prev) => {
      const updated = [...prev];
      const creator = { ...updated[index] };
      creator.videos = [...creator.videos, { id: getNextId(creator.videos), rateKey: '', crossPostingKeys: new Set([]) }];
      updated[index] = creator;
      return updated;
    });
  };

  const handleRemoveVideo = (index, videoIndex) => {
    setSelectedCreatorsData((prev) => {
      const updated = [...prev];
      const creator = { ...updated[index] };
      creator.videos = creator.videos.filter((_, i) => i !== videoIndex);
      updated[index] = creator;
      return updated;
    });
  };

  const handleOptionChange = (index, option, value, videoOption, videoIndex) => {
    setSelectedCreatorsData((prev) => {
      const updated = [...prev];
      const creator = { ...updated[index] };
      if (option === 'videos') {
        const videos = [...creator.videos];
        const video = { ...videos[videoIndex] };
        if (videoOption === 'crossPostingKeys') {
          video.crossPostingKeys = new Set([...value]);
        } else if (videoOption === 'rateKey') {
          video.crossPostingKeys = new Set();
          video[videoOption] = value;
        } else {
          video[videoOption] = value;
        }
        videos[videoIndex] = video;
        creator.videos = videos;
      } else {
        creator[option] = value;
      }
      creator.basePrice = calculateBasePrice(creator, creator.videos);
      creator.adjustedPrice = calculateAdjustedPrice(creator.basePrice, creator.usageRights);
      updated[index] = creator;
      return updated;
    });
  };

  const handleRemoveFromRoster = (creatorId) => {
    setSelectedItems((prev) => { prev.delete(creatorId); return new Set(prev); });
    setSelectedCreatorsData((prev) => prev.filter((item) => item.creator !== creatorId));
    toast.info('Creator removed from roster');
  };

  const handleCreatorSelect = (creatorId) => {
    if (prevCreators.has(creatorId)) return;
    const isSelected = selectedItems.has(creatorId);
    if (isSelected) {
      setSelectedItems((prev) => { prev.delete(creatorId); return new Set(prev); });
      setSelectedCreatorsData((prev) => prev.filter((item) => item.creator !== creatorId));
    } else {
      setSelectedItems((prev) => { prev.add(creatorId); return new Set(prev); });
      const full = creatorList.find((c) => c.creator === creatorId);
      setSelectedCreatorsData((prev) => [
        ...prev,
        { ...full, videos: [{ id: 1, rateKey: '', crossPostingKeys: new Set([]) }], basePrice: 0, usageRights: false, adjustedPrice: 0 },
      ]);
      if (!isLargeScreen) setIsDrawerOpen(true);
    }
  };

  /* ── Mutation ── */
  const { mutate: updateCreatorList, isLoading: updateLoading } = useMutation(
    client.campaigns.updateCreatorList,
    {
      onSuccess: (data) => {
        toast.success('Campaign updated', { description: `${data.length || 0} creators confirmed` });
        navigate(routes.collaborations);
      },
      onError: (error) => {
        alert(`Error adding creators: ${error.response?.data?.error || error.message}`);
      },
    }
  );

  const handleConfirmCreatorChanges = () => {
    if (!isTcc && (!hasAnyFunds || !canAfford)) { setAddBalanceOpen(true); return; }

    const hasBadData = selectedCreatorsData.some(
      (c) => !c.basePrice || c.basePrice === '0' || c.videos.length === 0 || c.videos.some((v) => !v.rateKey)
    );
    if (hasBadData) { setTriedNext(true); return; }

    const newCreators = selectedCreatorsData.map((creator) => {
      const price = parseFloat(creator.adjustedPrice);
      const nextId = getNextId(creator.videos);
      let crossPostOffset = 0;
      return {
        id: creator.creator,
        name: creator.creator,
        price,
        agencyRate: price * 0.95,
        pfphref: creator.pfphref,
        videos: [
          ...creator.videos.map((v) => {
            const { platform, promotion } = getPlatformAndPromotion(v.rateKey);
            return { id: v.id, parentId: null, promotionPlatform: platform, promotionType: promotion, platformLink: creator[platformToLink[platform]], following: creator[platformToFollowing[platform]] };
          }),
          ...creator.videos.flatMap((v) => {
            const result = [...v.crossPostingKeys].map((cp, i) => {
              const { platform, promotion } = getPlatformAndPromotion(cp);
              return { id: nextId + crossPostOffset + i, parentId: v.id, promotionPlatform: platform, promotionType: promotion, platformLink: creator[platformToLink[platform]], following: creator[platformToFollowing[platform]] };
            });
            crossPostOffset += v.crossPostingKeys.size;
            return result;
          }),
        ],
      };
    });

    updateCreatorList({ campaignId: parseInt(campaignId), newCreators });
  };

  /* ── Render ── */
  return (
    <>
      <UpdateBackdrop open={updateLoading} />
      <AddBalanceDialog
        open={addBalanceOpen}
        onClose={() => setAddBalanceOpen(false)}
        onSuccess={() => { refetchBalance(); setAddBalanceOpen(false); }}
      />

      <Box
        sx={{
          bgcolor: 'background.default',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
            : 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
          minHeight: '100vh',
          position: 'relative',
        }}
      >
        {/* Mobile FAB */}
        {!isLargeScreen && (
          <Box sx={(t) => ({ position: 'fixed', bottom: `calc(86px + env(safe-area-inset-bottom, 0px) + ${t.spacing(2)})`, right: t.spacing(2), zIndex: t.zIndex.modal + 3 })}>
            <Badge badgeContent={selectedCreatorsData.length} color="primary" overlap="circular">
              <Box
                component="button"
                onClick={() => setIsDrawerOpen((o) => !o)}
                sx={{
                  width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: !isTcc && !hasAnyFunds ? 'warning.main' : 'primary.main',
                  color: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  '&:hover': { transform: 'scale(1.07)', boxShadow: `0 6px 28px ${alpha(theme.palette.primary.main, 0.55)}` },
                }}
              >
                {!isTcc && !hasAnyFunds
                  ? <AccountBalanceWalletIcon sx={{ fontSize: '1.3rem' }} />
                  : <ShoppingCartCheckoutIcon sx={{ fontSize: '1.3rem' }} />}
              </Box>
            </Badge>
          </Box>
        )}

        {/* Main search area */}
        <Box sx={{ ...globalStyles.wholeHeightMinusToolbar, marginInlineEnd: { xs: 0, md: RightDrawerWidth }, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <LeftColumnSearch
            onCreatorSelect={handleCreatorSelect}
            data={creatorList}
            loading={creatorIsLoading}
            selectedItems={selectedItems}
          />
        </Box>

        {/* Right drawer */}
        <Drawer
          variant={isLargeScreen ? 'persistent' : 'temporary'}
          anchor="right"
          open={isLargeScreen || isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: RightDrawerWidth,
              maxWidth: '100vw',
              pt: '28px',
              px: '20px',
              overflowY: 'auto',
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              zIndex: (t) => t.zIndex.drawer,
              borderLeft: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === 'dark' ? '-8px 0 32px rgba(0,0,0,0.4)' : '-8px 0 32px rgba(0,0,0,0.06)',
              pb: { xs: `calc(86px + env(safe-area-inset-bottom, 0px) + 16px)`, md: '20px' },
            },
          }}
        >
          {!isLargeScreen && (
            <>
              <IconButton onClick={() => setIsDrawerOpen(false)} sx={{ ml: 'auto', display: 'block', mb: 1.5 }}>
                <ArrowRightIcon />
              </IconButton>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          <DrawerHeader creatorCount={selectedCreatorsData.length} campaignName={campaignDetails?.name} theme={theme} />

          <FundsSummary
            isLoadingFunds={isLoadingFunds}
            hasAnyFunds={hasAnyFunds}
            hasBlitzPayAccount={hasBlitzPayAccount}
            balance={balance}
            spendingAvailable={spendingAvailable}
            maxAffordable={maxAffordable}
            totalCost={totalNewCreatorCost}
            onAddBalance={() => setAddBalanceOpen(true)}
            theme={theme}
            isTcc={isTcc}
          />

          <Box sx={{ mb: 2}}>
            {selectedCreatorsData.length === 0
              ? <EmptyRoster theme={theme} />
              : selectedCreatorsData.map((creator) => (
                  <SelectedCreatorRow key={creator.creator} creator={creator} onRemove={handleRemoveFromRoster} theme={theme} />
                ))}
          </Box>

          {selectedCreatorsData.length > 0 && (
            <>
              <Divider sx={{ mb: 2 }} />
              <CampaignDetailsUI
                campaignDetails={campaignDetails}
                newCreators={selectedCreatorsData}
                handleAddVideo={handleAddVideo}
                handleOptionChange={handleOptionChange}
                handleRemoveVideo={handleRemoveVideo}
                handlePriceChange={handlePriceChange}
                triedNext={triedNext}
              />
            </>
          )}

          <ConfirmSection
            disabled={selectedCreatorsData.length < 1}
            onClick={handleConfirmCreatorChanges}
            count={selectedCreatorsData.length}
            canAfford={canAfford}
            hasAnyFunds={hasAnyFunds}
            theme={theme}
            isTcc={isTcc}
          />
        </Drawer>
      </Box>
    </>
  );
};

export default AddCreators;